import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { ViewCamera } from '../types';
import { toCanvasTrackball } from '../geometry/trackball';
import { isSameCamera, type OrbitState } from './orbitCamera';
import {
  initialOrbitGestureState,
  isOrbitButton,
  isRotating,
  isTurning,
  reduceOrbitGesture,
  toKeyDownInput,
  toKeyUpInput,
  toWheelInput,
  type OrbitGestureState,
  type OrbitInput,
} from './orbitGestures';

export type OrbitInteraction<C extends ViewCamera> = {
  orbit: OrbitState<C>;
  /**
   * Whether a rotation is under way, by pointer or by keyboard, including a press that has not
   * yet moved. A caller freezing its hit-testing reads this rather than `orbit.drag`.
   */
  isRotating: boolean;
  /**
   * Whether the reader has moved this camera themselves, latched once and never unset.
   *
   * For a caller that also *supplies* a camera - a fit recomputed from the viewport and the data -
   * and needs to know when to stop, because applying a new one replaces the reader's rotation and
   * zoom rather than merging with it. Covers the drag, the wheel and the zoom keys, the last of
   * which produces no event a caller could observe from outside.
   */
  hasReaderMovedCamera: boolean;
  /**
   * Whether the plot is actually moving, as opposed to merely being held.
   *
   * A press that has not travelled draws the same picture as no press at all, so dimming for it
   * would make almost the whole chart disappear on a click.
   */
  isTurning: boolean;
  /**
   * Put on the canvas in place of the ref.
   *
   * The wheel listener rides along with it, so it follows the element rather than the mount.
   */
  bindCanvas: (canvas: HTMLCanvasElement | null) => void;
  /**
   * Moves the camera to one the caller chose, now.
   *
   * Distinct from supplying a new `camera`, which is compared against the last one supplied and
   * ignored when it matches. That comparison is what lets a caller keep handing in a fit it
   * recomputes on every render without stamping on the reader's rotation - and it also means a
   * caller cannot ask for *the same* camera twice. A reader who fits, orbits away and fits again
   * is asking the same question twice and has to get the same answer, so the request has to be an
   * action rather than a value.
   */
  applyCamera: (camera: C) => void;
  /**
   * Whether the reader has moved the camera since a caller last set one, read now.
   *
   * A function rather than the `hasReaderMovedCamera` value beside it, and the difference is
   * timing. That value belongs to a render; this reads the reducer's own state, which `send`
   * updates synchronously. Anything deciding between frames rather than between renders - an
   * animation giving way to a gesture - has to ask this one, or it is asking about the camera as
   * it was a render ago.
   */
  hasReaderTakenCamera: () => boolean;
  /** Spread onto the canvas element. */
  canvasProps: {
    tabIndex: number;
    /**
     * Carries no `aria-label` on purpose: only the caller knows which fields are on the axes, and
     * a focusable element announced as an unnamed graphic is barely better than an unlabelled one.
     */
    role: 'img';
    style: React.CSSProperties;
    onPointerDown: React.PointerEventHandler<HTMLCanvasElement>;
    onPointerMove: React.PointerEventHandler<HTMLCanvasElement>;
    onPointerUp: React.PointerEventHandler<HTMLCanvasElement>;
    onPointerCancel: React.PointerEventHandler<HTMLCanvasElement>;
    onKeyDown: React.KeyboardEventHandler<HTMLCanvasElement>;
    onKeyUp: React.KeyboardEventHandler<HTMLCanvasElement>;
    onBlur: React.FocusEventHandler<HTMLCanvasElement>;
  };
  /**
   * Cursor position within the canvas, for hit-testing on hover.
   *
   * Takes a `MouseEvent` rather than a `PointerEvent` because it only reads coordinates, and a
   * caller telling a click from a drag needs it for both.
   */
  toLocalPoint: (event: React.MouseEvent<HTMLCanvasElement>) => {
    x: number;
    y: number;
  };
};

const toLocalPoint = (
  event: React.MouseEvent<HTMLCanvasElement>
): { x: number; y: number } => {
  const bounds = event.currentTarget.getBoundingClientRect();
  return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
};

/**
 * Orbit, zoom and keyboard control for a canvas chart, as React props over `reduceOrbitGesture`.
 *
 * - `wheel` is bound natively with `{ passive: false }`. React attaches its own wheel listener
 *   as passive, so `preventDefault` inside an `onWheel` prop is ignored - the chart zooms and
 *   the page scrolls underneath it at the same time.
 * - `touch-action: none` stops the browser claiming a drag as a pan or pinch before the pointer
 *   handlers ever see it.
 * - `tabIndex` plus arrow keys give it a keyboard path, with `role="img"` so the tab stop
 *   announces something; the caller supplies the `aria-label`.
 *
 * `camera` is a live input, not a seed, so a caller can reset the view. One supplied mid-gesture
 * lands when the gesture ends.
 */
export const useOrbitInteraction = <C extends ViewCamera>(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  camera: C
): OrbitInteraction<C> => {
  const [state, setState] = useState(() => initialOrbitGestureState(camera));
  const current = useRef<OrbitGestureState<C>>(state);

  const send = useCallback((input: OrbitInput<C>) => {
    const next = reduceOrbitGesture(current.current, input);
    if (next === current.current) return;
    current.current = next;
    setState(next);
  }, []);

  const applied = useRef(camera);
  useEffect(() => {
    if (isSameCamera(camera, applied.current)) return;
    applied.current = camera;
    send({ type: 'setCamera', camera });
  }, [camera, send]);

  const hasReaderTakenCamera = useCallback(
    () => current.current.hasReaderMovedCamera,
    []
  );

  const applyCamera = useCallback(
    (next: C) => {
      /* Recorded as the supplied one too. `applied` means "the last camera put into effect from
         outside the gesture", and this is one - so the effect above compares its prop against what
         is actually in effect rather than against an older camera it would then resend. */
      applied.current = next;
      send({ type: 'setCamera', camera: next });
    },
    [send]
  );

  const stopListening = useRef<VoidFunction | null>(null);

  /**
   * A callback ref rather than a mount effect, because the caller renders its canvas
   * conditionally against an empty state - so the element present at mount is often none, and the
   * one that appears when data arrives is a different node.
   */
  const bindCanvas = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      canvasRef.current = canvas;
      stopListening.current?.();
      stopListening.current = null;
      if (!canvas) {
        send({ type: 'detach' });
        return;
      }

      const onWheel = (event: WheelEvent) => {
        const input = toWheelInput(event);
        if (!input) return;
        event.preventDefault();
        send(input);
      };

      canvas.addEventListener('wheel', onWheel, { passive: false });
      stopListening.current = () =>
        canvas.removeEventListener('wheel', onWheel);
    },
    [canvasRef, send]
  );

  const onKeyDown = useCallback<React.KeyboardEventHandler<HTMLCanvasElement>>(
    event => {
      const input = toKeyDownInput(event);
      if (!input) return;
      event.preventDefault();
      send(input);
    },
    [send]
  );

  const onKeyUp = useCallback<React.KeyboardEventHandler<HTMLCanvasElement>>(
    event => send(toKeyUpInput(event)),
    [send]
  );

  const onBlur = useCallback(() => send({ type: 'blur' }), [send]);

  const onPointerDown = useCallback<
    React.PointerEventHandler<HTMLCanvasElement>
  >(
    event => {
      if (!isOrbitButton(event)) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.focus();
      send({
        type: 'pointerDown',
        ...toLocalPoint(event),
        sphere: toCanvasTrackball(event.currentTarget.getBoundingClientRect()),
      });
    },
    [send]
  );

  const onPointerMove = useCallback<
    React.PointerEventHandler<HTMLCanvasElement>
  >(event => send({ type: 'pointerMove', ...toLocalPoint(event) }), [send]);

  const onPointerUp = useCallback<React.PointerEventHandler<HTMLCanvasElement>>(
    event => {
      if (!isOrbitButton(event)) return;
      event.currentTarget.releasePointerCapture(event.pointerId);
      send({ type: 'pointerUp' });
    },
    [send]
  );

  const onPointerCancel = useCallback(
    () => send({ type: 'pointerCancel' }),
    [send]
  );

  return {
    orbit: state.orbit,
    isRotating: isRotating(state),
    isTurning: isTurning(state),
    hasReaderMovedCamera: state.hasReaderMovedCamera,
    hasReaderTakenCamera,
    bindCanvas,
    applyCamera,
    toLocalPoint,
    canvasProps: {
      tabIndex: 0,
      role: 'img',
      style: {
        display: 'block',
        touchAction: 'none',
        /* Inset rather than removed: a UA draws the ring outside the border box, where an
           `overflow` ancestor clips it, and this canvas is a real control with a keyboard path. */
        outlineOffset: '-2px',
        cursor: state.orbit.drag ? 'grabbing' : 'grab',
      },
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onKeyDown,
      onKeyUp,
      onBlur,
    },
  };
};
